import PropTypes from 'prop-types';
import React from 'react';
import moment from 'moment';
import LessonPause from './LessonPause';
import styled from 'styled-components';
import Clear from '@material-ui/icons/Clear';

import { PvBox, PvButtonOutline, PvUppercaseText, PvInput, ShadowedBox, ErrorLabel, PvCaret, BoldLabel } from 'styles/CustomComponents';
import { PvCheckbox } from 'components/form/PvCheckbox';
import { PvDatePicker } from 'components/form/PvDatePicker';
import { Flex, Label, Panel, Heading, Text } from 'rebass';
import { Translate } from 'react-redux-i18n';
import uniqueId from 'lodash/uniqueId';
import { PvSelect } from 'components/form/PvSelect';
import { PvClockInput} from "components/form/PvClockInput";
import { isDayBlockedLessonEdit } from '../search/isDayBlocked';

const PvClearIcon = styled(Clear)`
  color: ${props => props.theme.colors.black};
  fontSize: 28px;
  &:hover {
    background-color: #ccc;
    cursor: pointer;
  }
`;

const PvPanel = styled(Panel)`
  box-shadow: 0 0 0 2px #ebebeb;
  border:0px;
`;

const PvPanelHeader = styled(Panel.Header)`
  color: ${props => props.theme.colors.green};
  font-size: 14px;
  font-weight: 700;
  &:hover {
    background-color: ${props => props.theme.colors.grays[1]};
    cursor: pointer;
  }
  background-color: ${props => props.theme.colors.grays[2]};
  display:flex;
  justify-content: space-between;
  box-shadow: 0 0 0 2px #ebebeb;
  border:0px;
`;

const PvGreenCaret = styled(PvCaret)`
  &:before {
    border-bottom: 5px solid ${props => props.theme.colors.green};
  }
  &:after {
    border-top: 5px solid ${props => props.theme.colors.green};
  }
`;

const ApplicantInfo = ({ applicants = [] }) => {
  const InfoHeading = (props) => <Heading mb={3} fontSize='19px'>{props.children}</Heading>

  if (applicants.length === 1) {
    const { lastName, firstNames, trafiId } = applicants[0]
    return (
      <InfoHeading>
        <PvUppercaseText>{lastName}</PvUppercaseText>
        <span>{` ${firstNames} (${trafiId})`}</span>
      </InfoHeading>
    )
  } else {
    // Poor implementation for multiple selected applicants but parent component does not allow multiple selections
    return (
      <InfoHeading>
        <Translate value='driving_lesson.applicant_info_multiple' count={applicants.length} />
      </InfoHeading>
    )
  }
}

class Lesson extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      datePickerOpen: false,
      showLessonTopics: false
    };

    const addLessonPause = () => {
      let newLessonPauses = [...this.props.lessonData.lessonPauses] || [];
      const newLessonPause = {
        selectedPauseType: '',
        pauseStartTime: '',
        pauseEndTime: '',
        id: uniqueId('lessonpause_'),
        errors: this.validateLessonPauseInputs('', '', '')
      };
      newLessonPauses.push(newLessonPause);
      this.props.updateLessonPauses({lessonPauses: newLessonPauses, lessonKey: this.props.lessonData.id});
    };

    const updateLessonPause = (id, updatedData) => {
      let updatedLessonPauses = [...this.props.lessonData.lessonPauses] || [];
      let idx = updatedLessonPauses.findIndex((lp) => lp.id === id );
      updatedLessonPauses[idx] = Object.assign(updatedLessonPauses[idx], updatedData);

      updatedLessonPauses = this.validateAllPauses(updatedLessonPauses);
      this.props.updateLessonPauses({lessonPauses: updatedLessonPauses, lessonKey: this.props.lessonData.id});
    };

    const updateLessonData = (updatedData) => {
      let newLessonData = Object.assign({}, this.props.lessonData, updatedData);

      const errors = this.validateInputs(
        newLessonData.lessonDate,
        newLessonData.startTime,
        newLessonData.endTime,
        newLessonData.class,
        newLessonData.subject);
      
      newLessonData.errors = errors;
      
      if (newLessonData.startTime || newLessonData.endTime) {
        newLessonData.lessonPauses = this.validateAllPauses(newLessonData.lessonPauses, newLessonData.startTime, newLessonData.endTime);
      }

      this.props.updateLesson(newLessonData);
    };

    const isToday = (day) => {
      return moment(day).isSame(moment(), "day")
    };

    const validateInputs = (
      lessonDate,
      startTime,
      endTime,
      examClass,
      subject
    ) => {

      let errors = {};

      if(!moment(lessonDate).isValid() || moment(lessonDate).isAfter(moment(), "day")) {
        errors["lessonDate"] = "driving_lesson.errors.lessonDate";
      }
      if(!moment(startTime, ["h:mm","HH:mm"], true).isValid()
        || !moment(startTime, ["h:mm","HH:mm"], true).isBefore(moment(endTime, ["h:mm","HH:mm"], true))
        || (isToday(lessonDate) && moment(startTime, ["h:mm","HH:mm"], true).isAfter(moment()))
        || moment(lessonDate).isAfter(moment(), "day")
      ) {
        errors["startTime"] = "driving_lesson.errors.startTime";
      }
      if(!moment(endTime, ["h:mm","HH:mm"], true).isValid()
        ||!moment(endTime, ["h:mm","HH:mm"], true).isAfter(moment(startTime, ["h:mm","HH:mm"], true))
        || (isToday(lessonDate) && moment(endTime, ["h:mm","HH:mm"], true).isAfter(moment()))
        || moment(lessonDate).isAfter(moment(), "day")
      ) {
        errors["endTime"] = "driving_lesson.errors.endTime";
      }
      if(!examClass || examClass.length === 0) {
        errors["examClass"] = "driving_lesson.errors.examClass";
      }
      if(!subject || subject.length === 0) {
        errors["subject"] = "driving_lesson.errors.examSubject";
      }

      return errors;
    };

    const nestedPause = (pauseStart1, pauseEnd1, pauseStart2, pauseEnd2) => {
      return (pauseStart1.isBefore(pauseStart2) && pauseEnd1.isAfter(pauseEnd2)) || (pauseStart2.isBefore(pauseStart1) && pauseEnd2.isAfter(pauseEnd1));
    };

    const overlappping = (id1, pauseStart1, pauseEnd1, id2, pauseStart2, pauseEnd2) => {

      if (id1 === id2) {
        return {};
      }

      let errors = {};

      if (pauseStart1.isAfter(pauseStart2) && pauseStart1.isBefore(pauseEnd2)) {
        errors["pauseStartTime"] = "driving_lesson.errors.pause_start_time_not_in_range";

      }
      if (pauseEnd1.isAfter(pauseStart2) && pauseEnd1.isBefore(pauseEnd2)) {
        errors["pauseEndTime"] = "driving_lesson.errors.pause_end_time_not_in_range";
      }

      if (nestedPause(pauseStart1, pauseEnd1, pauseStart2, pauseEnd2)) {
        errors["pauseStartTime"] = "driving_lesson.errors.pause_nested";
        errors["pauseEndTime"] = "driving_lesson.errors.pause_nested";
      }

      return errors;

    };

    const validateLessonPauseInputs = (
      selectedPauseType,
      pauseStartTime,
      pauseEndTime,
      id,
      newLessonStart,
      newLessonEnd
    ) => {
  
      let errors = {};
      let pauseStartMoment = moment(pauseStartTime, ["h:mm","HH:mm"], true);
      let pauseEndMoment = moment(pauseEndTime, ["h:mm","HH:mm"], true);
      let lessonStartMoment = newLessonStart ? moment(newLessonStart, ["h:mm","HH:mm"], true) : moment(this.props.lessonData.startTime, ["h:mm","HH:mm"], true);
      let lessonEndMoment =  newLessonEnd ? moment(newLessonEnd, ["h:mm","HH:mm"], true) : moment(this.props.lessonData.endTime, ["h:mm","HH:mm"], true);

      if(!pauseStartMoment.isValid()
        || !pauseStartMoment.isBefore(pauseEndMoment)) {
          errors["pauseStartTime"] = "driving_lesson.errors.pause_start_time";
        }
      else if(!pauseStartMoment.isBetween(lessonStartMoment,
        lessonEndMoment, null, '[)')) {
          errors["pauseStartTime"] = "driving_lesson.errors.pause_start_time_not_in_range";
        }

      if(!pauseEndMoment.isValid()
        ||!pauseEndMoment.isAfter(pauseStartMoment)) {
          errors["pauseEndTime"] = "driving_lesson.errors.pause_end_time";
        }
      else if(!pauseEndMoment.isBetween(lessonStartMoment,
        lessonEndMoment, null, '(]')) {
          errors["pauseEndTime"] = "driving_lesson.errors.pause_end_time_not_in_range";
        }


      if(!selectedPauseType || selectedPauseType.length === 0) {
        errors["selectedPauseType"] = "driving_lesson.errors.selected_pause_type";
      }
      
      return errors;
    };

    const validateAllPauses = (updatedLessonPauses, newLessonStart, newLessonEnd) => {

      updatedLessonPauses.forEach(p => {
          let errors = this.validateLessonPauseInputs(p.selectedPauseType, p.pauseStartTime, p.pauseEndTime, p.id, newLessonStart, newLessonEnd);
          p.errors = errors;
        });

      updatedLessonPauses.forEach( updatedPauseData => {
        updatedPauseData.errors = Object.assign(updatedPauseData.errors, this.validateLessonPauseOverlaps(updatedPauseData, updatedLessonPauses));
      });
      return updatedLessonPauses;
    };

    const validateLessonPauseOverlaps = (lessonPause, allPauses) => {
      let errors = {};
      let pauseStartMoment = moment(lessonPause.pauseStartTime, ["h:mm","HH:mm"], true);
      let pauseEndMoment = moment(lessonPause.pauseEndTime, ["h:mm","HH:mm"], true);

      allPauses.forEach(p =>
        { Object.assign(errors, overlappping(lessonPause.id, pauseStartMoment, pauseEndMoment, p.id, moment(p.pauseStartTime,["h:mm","HH:mm"], true), moment(p.pauseEndTime,["h:mm","HH:mm"], true)))}
      );
      return errors;
    };

    const updateLessonStartTime = (startTime) => {

      let start = startTime.replace('.', ':');
      let startMoment = moment(start, ["h:mm", "HH:mm"], true);

      if (startMoment.isValid()) {

        let endMoment = startMoment.add(50, "minutes");
        let end = endMoment.format("HH:mm");

        this.updateLessonData({startTime: start, endTime: end});
      } else {
        this.updateLessonData({startTime: start});
      }
    };

    const updateLessonTopic = (value, lessonCode) => {

      let topicCodes = [...new Set(this.props.lessonData.drivingTopicCodes)] || [];

      if (!value) {
        topicCodes = topicCodes.filter(code => code !== lessonCode);
      }
      else {
        topicCodes.push(lessonCode);
      }

      this.updateLessonData({drivingTopicCodes : topicCodes});
    };

    const availableExamClasses = () => {
      return [props.courseExamClasses].map(ec => ({value: ec, label: ec}));
    };

    const availableSubjects = () => {
      const selectedClass = this.props.lessonData.class;
      const availableLessons = !!this.props.availableLessons && this.props.availableLessons.length > 0 ? this.props.availableLessons.filter(l => (this.props.addedLessons && !this.props.addedLessons.includes(l.entityId))) : [];
      return !!selectedClass && availableLessons.filter(lesson => (lesson.examClasses || []).map(item => item.name).some(item => item === selectedClass));
    };

    this.updateLessonData = updateLessonData.bind(this);
    this.addLessonPause = addLessonPause.bind(this);
    this.updateLessonPause = updateLessonPause.bind(this);
    this.validateInputs = validateInputs.bind(this);
    this.validateLessonPauseInputs = validateLessonPauseInputs.bind(this);
    this.validateAllPauses = validateAllPauses.bind(this);
    this.validateLessonPauseOverlaps = validateLessonPauseOverlaps.bind(this);
    this.updateLessonStartTime = updateLessonStartTime.bind(this);
    this.availableExamClasses = availableExamClasses.bind(this);
    this.availableSubjects = availableSubjects.bind(this);
    this.updateLessonTopic = updateLessonTopic.bind(this);
  }

  componentWillMount() {
    if(!!this.props && !!this.props.lessonData) {
      const errors = this.validateInputs(
        this.props.lessonData.lessonDate,
        this.props.lessonData.startTime,
        this.props.lessonData.endTime,
        this.props.lessonData.class,
        this.props.lessonData.subject);

      const newLessonData = Object.assign({}, this.props.lessonData, {errors: errors});

      this.props.updateLesson(newLessonData);
    }
  }

  render() {

    const {
      datePickerOpen
    } = this.state;

    const lessonPauseComponents = (this.props.lessonData.lessonPauses || []).map((lessonPause) => {
      return <LessonPause
        lessonPauseTypes= {this.props.pauseTypes}
        selectedPauseType= {lessonPause.selectedPauseType}
        pauseStartTime= {lessonPause.pauseStartTime}
        pauseEndTime= {lessonPause.pauseEndTime}
        removeLessonPause= {
          (id) => {
            if(!!id) {
              let newLessonPauses = [...this.props.lessonData.lessonPauses] || [];
              let idx = newLessonPauses.findIndex((lessonPause) => lessonPause.id === id);
              newLessonPauses.splice(idx, 1);
              this.updateLessonData({lessonPauses: newLessonPauses, lessonKey: this.props.lessonData.id});
            }
          }
        }  
        updateLessonPause= {this.updateLessonPause}     
        key={lessonPause.id} 
        id={lessonPause.id}
        generalLoading={this.props.generalLoading}
        errors={lessonPause.errors}
      />
    });

    const lessonTopics = (this.props.lessonTopics || []).map((lessonTopic) => {
      return <PvBox display='block' px={2} pb={3} w={1} key={lessonTopic.code + "-" + lessonTopic.topic}>
        <Label mb={2}>
          <PvCheckbox type="checkbox" defaultChecked={!!this.props.lessonData.drivingTopicCodes && this.props.lessonData.drivingTopicCodes.includes(lessonTopic.code) } mr={3}
                      onChange={(e) => this.updateLessonTopic(e.target.checked, lessonTopic.code)}
          />
          {lessonTopic.code}. {lessonTopic.topic}
        </Label>
      </PvBox>
    });

    return (
      <li>
        <ShadowedBox>
          <PvBox px={3} pb={3}>
            <PvBox display='inline-block' w={1} mb={3}>
              <Flex flexWrap='wrap' justifyContent='flex-end'>
                {!!this.props.showDeleteButton && <PvClearIcon onClick={() => this.props.removeLesson(this.props.lessonData.id)} />}
              </Flex>
            </PvBox>
            <PvBox mb={3}>
              <PvBox>
                {this.props.selectedApplicants && <ApplicantInfo applicants={this.props.selectedApplicants} />}
                <Flex flexWrap='wrap' alignItems='flex-end'>
                  <PvBox display='inline-block' px={2} pb={3}>
                    <ErrorLabel mb={2} error={this.props.lessonData.errors.lessonDate}>
                      <Translate value='driving_lesson.lesson_date' /> *
                    </ErrorLabel>
                    <PvDatePicker
                      date={this.props.lessonData.lessonDate}
                      onDateChangeFn={(date) => this.updateLessonData({lessonDate: date})}
                      focused={datePickerOpen}
                      onFocusChangeFn={({ focused }) => this.setState({ datePickerOpen: focused })}
                      isDayBlocked={isDayBlockedLessonEdit}
                    />
                  </PvBox>
                  <PvBox  display='inline-block' px={2} pb={3} w={[1/3, 1/4, 1/7]}>
                    <ErrorLabel mb={2} error={this.props.lessonData.errors.startTime}>
                      <Translate value='driving_lesson.start_time' /> *
                    </ErrorLabel>
                    <PvClockInput
                      value={this.props.lessonData.startTime}
                      onChangeFn={(e) =>this.updateLessonStartTime(e)} />
                  </PvBox>
                  <Label display='inline-block' pb={30}>
                    -
                  </Label>
                  <PvBox display='inline-block' px={2} pb={3} w={[1/3, 1/4, 1/7]}>
                    <ErrorLabel mb={2} error={this.props.lessonData.errors.endTime}>
                      <Translate value='driving_lesson.end_time' /> *
                    </ErrorLabel>
                    <PvClockInput
                      value={this.props.lessonData.endTime}
                      onChangeFn={(time) => this.updateLessonData({endTime: time})}
                    />
                  </PvBox>
                  { !!this.props.lessonData && this.props.lessonData.subject.lessonType === "PRO_DRIVING" &&
                  <PvBox display='inline-block' px={2} pb={3}>
                    <Label mb={2}>
                      <PvCheckbox type="checkbox" defaultChecked={this.props.lessonData.beneficialDriving}
                        onChange={(e) => this.updateLessonData({beneficialDriving: e.target.checked})}
                      />
                      <Translate style={{paddingLeft: '5px'}} value='driving_lesson.beneficial_driving' />
                    </Label>
                  </PvBox>
                  }
                </Flex>
              </PvBox>

              <PvBox my={3}>
              {!this.props.editMode &&
                <Flex flexWrap='wrap' alignItems='flex-end'>
                    <PvBox display='inline-block' px={2} pb={3} w={[1, 1, 2 / 5]}>
                      <ErrorLabel mb={2} error={this.props.lessonData.errors.examClass}>
                        <Translate value='driving_lesson.class' /> *
                      </ErrorLabel>
                      <PvSelect
                        options={this.availableExamClasses()}
                        value={{ value: this.props.lessonData.class, label: this.props.lessonData.class }}
                        onChange={(option) => this.updateLessonData({ class: option.value, subject: '' })}
                        isLoading={this.props.generalLoading}
                      />
                    </PvBox>
                  <PvBox display='inline-block' px={2} pb={3} w={[1, 1, 3/5]}>
                    <ErrorLabel mb={2} error={this.props.lessonData.errors.subject}>
                      <Translate value='driving_lesson.subject' /> *
                    </ErrorLabel>
                    <PvSelect
                      options={!!this.availableSubjects() ? this.availableSubjects().map(subject => {return {value: subject, label: subject.description};}) : []}
                      value={{value: this.props.lessonData.subject.lessonId, label: this.props.lessonData.subject.description }}
                      onChange={(option) => this.updateLessonData({subject: option.value})}
                      isLoading={this.props.generalLoading}
                    />
                  </PvBox>
                </Flex>
                }
                {!!this.props.editMode &&
                <Flex flexWrap='wrap' alignItems='flex-end'>
                    <PvBox display='inline-block' px={2} pb={3} w={[1, 1, 2 / 5]}>
                      <BoldLabel mb={2} >
                        <Translate value='driving_lesson.class' />
                      </BoldLabel>
                    <Text>{this.props.lessonData.class}</Text>
                    </PvBox>
                  <PvBox display='inline-block' px={2} pb={3} w={[1, 1, 3/5]}>
                    <BoldLabel>
                      <Translate value='driving_lesson.subject' />
                    </BoldLabel>
                    <Text>
                      {this.props.lessonData.subject.description}
                    </Text>
                  </PvBox>
                </Flex>
              }
              </PvBox>

              <PvBox my={3}>
                <Flex flexWrap='wrap' alignItems='flex-end'>
                  <PvBox display='inline-block' px={2} pb={3} w={[1, 1, 1/5]}>
                    <BoldLabel mb={2}>
                      <Translate value='driving_lesson.plate_number' />
                    </BoldLabel>
                    <PvInput value={this.props.lessonData.plateNumber}
                      onChange={(e) => this.updateLessonData({plateNumber: e.target.value})} />
                  </PvBox>
                  <PvBox display='inline-block' px={2} pb={3} w={[1, 1, 1/5]}>
                    <BoldLabel mb={2}>
                      <Translate value='driving_lesson.kilometrage' />
                    </BoldLabel>
                    <PvInput value={this.props.lessonData.kilometrage}
                      onChange={(e) => this.updateLessonData({kilometrage: e.target.value})} />
                  </PvBox>
                  <PvBox display='inline-block' px={2} pb={3} w={[1, 1, 3/5]}>
                    <BoldLabel mb={2}>
                      <Translate value='driving_lesson.additional_info' />
                    </BoldLabel>
                    <PvInput maxlength="230" value={this.props.lessonData.additionalInfo}
                      onChange={(e) => this.updateLessonData({additionalInfo: e.target.value})} />
                  </PvBox>
                  <PvBox display='inline-block' px={2} pb={3} w={[1, 1, 1]}>
                      <PvPanel>
                        <PvPanelHeader onClick={()=> this.setState({showLessonTopics : !this.state.showLessonTopics})}>
                          <Translate value="driving_lesson.lessonTopics"/> <PvGreenCaret/>
                        </PvPanelHeader>
                        {!!this.state.showLessonTopics &&
                          <Flex flexDirection="row">
                            <PvBox display='inline-block' px={2} pb={3} pt={3} w={[0.5, 0.5, 0.5]}>
                              {lessonTopics.slice(0, Math.ceil(lessonTopics.length / 2)) }
                            </PvBox>
                            <PvBox display='inline-block' px={2} pb={3} pt={3} w={[0.5, 0.5, 0.5]}>
                              {lessonTopics.slice(Math.ceil(lessonTopics.length / 2), lessonTopics.length)}
                            </PvBox>
                          </Flex>
                        }
                      </PvPanel>
                    </PvBox>

                </Flex>
              </PvBox>
              <PvBox my={3}>
                <Flex flexWrap='wrap' alignItems='flex-end'>
                  {lessonPauseComponents}
                  <PvButtonOutline onClick={() => this.addLessonPause()}>
                    <Translate value='driving_lesson.add_lesson_pause' />
                  </PvButtonOutline>
                </Flex>
              </PvBox>
            </PvBox>
          </PvBox>
        </ShadowedBox>
      </li>
    );
  }  
}

Lesson.propTypes = {
  editMode: PropTypes.bool,
  error: PropTypes.object,
  generalLoading: PropTypes.bool,
  lessonData: PropTypes.object,
  updateLessonPauses: PropTypes.func,
  removeLesson: PropTypes.func,
  updateLesson: PropTypes.func,
  availableLessons: PropTypes.array,
  pauseTypes: PropTypes.array,
  errors: PropTypes.object,
  showDeleteButton: PropTypes.bool,
  addedLessons: PropTypes.array,
  selectedApplicants: PropTypes.array,
  courseExamClasses: PropTypes.array,
};

export default Lesson;



// WEBPACK FOOTER //
// ./src/containers/driving/Lesson.jsx